import { Trash2 } from "lucide-react";
import {
  Form,
  minValue,
  required,
  useCreate,
  useDelete,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";

import { DateInput } from "@/components/admin/date-input";
import { FileInput } from "@/components/admin/file-input";
import { SaveButton } from "@/components/admin/form";
import { NumberInput } from "@/components/admin/number-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { AttachmentField } from "../../notes/AttachmentField";
import {
  timestampToDateOnly,
  toDateOnly,
  toMonth,
} from "../../providers/commons/financeSchedule";
import type { FinanceExpense } from "../../types";
import { expenseCategoryChoices } from "../financeOptions";
import { dayToTimestamp, toNullableText } from "../formValues";
import { useFinanceErrorHandler } from "../useFinanceErrorHandler";

const RESOURCE = "finance_expenses";

export interface ExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  expense?: FinanceExpense | null;
}

export const ExpenseDialog = ({
  open,
  onClose,
  expense,
}: ExpenseDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const onError = useFinanceErrorHandler();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const record = useMemo(
    () =>
      expense
        ? { ...expense, paid_on: timestampToDateOnly(expense.paid_at) }
        : {
            category: "other",
            competence_month: toMonth(new Date()),
            due_date: toDateOnly(new Date()),
            files: [],
          },
    [expense],
  );
  const done = (message: string) => () => {
    notify(message);
    onClose();
  };

  const handleSubmit = async (values: FieldValues) => {
    const data: Partial<FinanceExpense> = {
      description: values.description.trim(),
      category: values.category,
      supplier: toNullableText(values.supplier),
      amount: Number(values.amount),
      competence_month: toMonth(values.competence_month),
      due_date: values.due_date,
      paid_at: dayToTimestamp(values.paid_on, expense?.paid_at),
      files: values.files ?? [],
      notes: toNullableText(values.notes),
    };
    if (expense) {
      await update(
        RESOURCE,
        { id: expense.id, data, previousData: expense },
        { onSuccess: done("crm.finance.expenses.updated"), onError },
      );
      return;
    }
    await create(
      RESOURCE,
      { data },
      { onSuccess: done("crm.finance.expenses.created"), onError },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-9/10 overflow-y-auto lg:max-w-2xl">
        {open && (
          <Form
            record={record}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {translate(
                  expense
                    ? "crm.finance.expenses.edit"
                    : "crm.finance.expenses.create",
                )}
              </DialogTitle>
            </DialogHeader>
            <TextInput
              source="description"
              label="crm.finance.fields.description"
              validate={required()}
              helperText={false}
              autoFocus
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SelectInput
                source="category"
                label="crm.finance.expenses.fields.category"
                choices={expenseCategoryChoices}
                validate={required()}
                helperText={false}
              />
              <TextInput
                source="supplier"
                label="crm.finance.expenses.fields.supplier"
                helperText={false}
              />
              <NumberInput
                source="amount"
                label="crm.finance.fields.amount"
                min={0}
                step={0.01}
                validate={[required(), minValue(0)]}
                helperText={false}
              />
              <DateInput
                source="competence_month"
                label="crm.finance.fields.competence_month"
                validate={required()}
                helperText="crm.finance.fields.competence_month_help"
              />
              <DateInput
                source="due_date"
                label="crm.finance.fields.due_date"
                validate={required()}
                helperText={false}
              />
              <DateInput
                source="paid_on"
                label="crm.finance.fields.paid_at"
                helperText={false}
              />
            </div>
            <FileInput
              source="files"
              label="crm.finance.expenses.fields.files"
              multiple
            >
              <AttachmentField source="src" title="title" target="_blank" />
            </FileInput>
            <TextInput
              source="notes"
              label="crm.finance.fields.notes"
              multiline
              helperText={false}
            />
            <DialogFooter className="w-full gap-2 sm:justify-between">
              {expense ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive"
                  disabled={isDeleting}
                  onClick={() =>
                    deleteOne(
                      RESOURCE,
                      { id: expense.id, previousData: expense },
                      {
                        onSuccess: done("crm.finance.expenses.deleted"),
                        onError,
                      },
                    )
                  }
                >
                  <Trash2 />
                  {translate("ra.action.delete")}
                </Button>
              ) : (
                <span />
              )}
              <SaveButton />
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
